import { UploadScanStatus } from 'src/types';
import { canDownloadFile, getStatusColumnText } from 'src/utils';
import { DateFormats, formatDate } from 'src/utils/time';
import { readableFileSize } from '../FileUpload/helpers';
import { handleErrorToast } from '../Toast';
import { DeletedEvidenceFile } from './DeletedEvidenceFile';
import { useGetReadURL } from './hooks/useGetReadURL';
import { UploadedEvidenceFile } from './types';
import {
  EvidenceTitle,
  FileNameText,
  FilesTable,
  FlaggedFilesError,
  FlaggedFilesMobileError,
  TableCell,
  TableFileNameCellLink,
  TableHeadingRow,
  TableRow,
  UploadedEvidenceContainer,
} from './UploadedEvidence.styles';

type LocalisedText = {
  tableTitle: string;
  webErrorMessage: string;
  mobileErrorMessage: string;
  nameHeading: string;
  addedHeading: string;
  statusHeading: string;
  failedToOpenFileMessage: string;
  fileStatuses: {
    pendingUpload: string;
    passedUpload: string;
    infectedUpload: string;
    readyForDownload: string;
  };
};

const enGBText = (caseManagerView: boolean): LocalisedText => ({
  tableTitle: 'Evidence',
  webErrorMessage: caseManagerView
    ? 'Flagged files: The case lead should inform the reporter that their file has been flagged by the security check for possible viruses or malware.'
    : 'Flagged files: If your file(s) has been flagged, please message the case manager',
  mobileErrorMessage: 'Flagged files: message your case manager',
  nameHeading: 'Name',
  addedHeading: 'Added',
  statusHeading: 'Status',
  failedToOpenFileMessage: 'Failed to open file. Please try again',
  fileStatuses: {
    infectedUpload: 'Flagged by security check',
    passedUpload: 'Passed security check',
    pendingUpload: 'Security check in progress',
    readyForDownload: 'Ready for download',
  },
});

const generateFileNameText = (
  baseFileName: string,
  index: number,
  file: UploadedEvidenceFile,
) => {
  const defaultFileName = `${baseFileName}${index + 1}`;
  return {
    defaultFileName,
    fileNameText: `${defaultFileName}.${file.fileExtension}${
      file.size ? ` ${readableFileSize(file.size)}` : ''
    }`,
  };
};

interface Props {
  files: UploadedEvidenceFile[];
  reportId: string;
  caseManagerView?: boolean;
  localisedText?: LocalisedText;
  'data-testid'?: string;
  hideTitle?: boolean;
  baseFileName?: string;
  downloadEvidenceDisabled?: boolean;
}

export const UploadedEvidence = ({
  files,
  caseManagerView = false,
  localisedText = enGBText(caseManagerView),
  reportId,
  hideTitle,
  baseFileName = 'EvidenceUploaded',
  downloadEvidenceDisabled,
  ...props
}: Props) => {
  const dateFormat = DateFormats.DAY_MONTH_YEAR;
  const hasFlaggedFiles = files.some(
    (file) => file.status === UploadScanStatus.INFECTED,
  );

  const { getReadUrl } = useGetReadURL(caseManagerView, reportId);

  const handleOpenFile = async (file: UploadedEvidenceFile) => {
    if (downloadEvidenceDisabled) return;
    if (!canDownloadFile(file.status)) return;

    try {
      const url = await getReadUrl(file);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      handleErrorToast({
        toastMessage: localisedText.failedToOpenFileMessage,
      });
    }
  };

  const showTitle = !hideTitle;
  const canDownloadEvidence = !downloadEvidenceDisabled;

  const isFileDeleted = (file: UploadedEvidenceFile) => {
    return file.dataDeletedAt || Boolean(file.fileS3Path?.includes('_deleted'));
  };

  return (
    <UploadedEvidenceContainer data-testid={props['data-testid']}>
      {showTitle && <EvidenceTitle>{localisedText.tableTitle}</EvidenceTitle>}
      {hasFlaggedFiles && (
        <>
          <FlaggedFilesError data-testid="flagged-files-error">
            {localisedText.webErrorMessage}
          </FlaggedFilesError>
          <FlaggedFilesMobileError>
            {localisedText.mobileErrorMessage}
          </FlaggedFilesMobileError>
        </>
      )}
      <div>
        <FilesTable>
          <thead>
            <TableHeadingRow>
              <td>{localisedText.nameHeading}</td>
              <td>{localisedText.addedHeading}</td>
              <td>{localisedText.statusHeading}</td>
            </TableHeadingRow>
          </thead>
          <tbody>
            {files.map((file, index) => {
              const { defaultFileName, fileNameText } = generateFileNameText(
                baseFileName,
                index,
                file,
              );

              if (isFileDeleted(file)) {
                return (
                  <DeletedEvidenceFile
                    key={`deleted-${file.fileS3Path || 'file'}-${index}`}
                    file={file}
                    index={index}
                    baseFileName={baseFileName}
                  />
                );
              }
              const scanStatus = getStatusColumnText(
                file.status,
                localisedText.fileStatuses,
                downloadEvidenceDisabled,
                !caseManagerView,
              );

              return (
                <TableRow
                  key={index}
                  data-testid={`uploaded-file-${defaultFileName}`}
                >
                  <td data-testid={`uploaded-evidence-file-name-${index}`}>
                    {canDownloadEvidence ? (
                      <TableFileNameCellLink
                        onClick={() => handleOpenFile(file)}
                        data-testid={`uploaded-evidence-file-link-${index}`}
                        scanStatus={file.status}
                      >
                        {fileNameText}
                      </TableFileNameCellLink>
                    ) : (
                      <FileNameText scanStatus={file.status}>
                        {fileNameText}
                      </FileNameText>
                    )}
                  </td>
                  <TableCell
                    data-testid={`uploaded-evidence-file-date-${index}`}
                  >
                    {formatDate(file.dateAdded, dateFormat)}
                  </TableCell>
                  <TableCell
                    data-testid={`uploaded-evidence-file-status-${index}`}
                  >
                    {scanStatus}
                  </TableCell>
                </TableRow>
              );
            })}
          </tbody>
        </FilesTable>
      </div>
    </UploadedEvidenceContainer>
  );
};
